import React, { useState, useEffect } from 'react';
import { groupBookingsService } from '../../../services/groupBookingsService';

const GroupDetails = ({ group, onClose, onUpdate }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [participantForm, setParticipantForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    package_selected: '',
    add_ons: [],
    total_amount: 0
  });

  useEffect(() => {
    fetchGroupDetails();
  }, [group.id]);

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      const data = await groupBookingsService.getById(group.id);
      setParticipants(data.participants || []);
    } catch (error) {
      console.error('Error fetching group details:', error);
      setParticipants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddParticipant = async (e) => {
    e.preventDefault();
    try {
      await groupBookingsService.addParticipant(group.id, participantForm);
      fetchGroupDetails();
      setShowAddParticipant(false);
      setParticipantForm({
        client_name: '',
        client_email: '',
        client_phone: '',
        package_selected: '',
        add_ons: [],
        total_amount: 0
      });
      onUpdate(); // Refresh parent component
    } catch (error) {
      console.error('Error adding participant:', error);
      alert('Failed to add participant. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getPaymentStatusBadge = (status) => {
    const statusClasses = {
      paid: 'payment-status paid',
      pending: 'payment-status pending',
      partial: 'payment-status partial',
      cancelled: 'payment-status cancelled'
    };
    return statusClasses[status] || 'payment-status';
  };

  const packages = JSON.parse(group.packages || '[]');
  const paymentSchedule = JSON.parse(group.payment_schedule || '[]');
  const coordinator = JSON.parse(group.coordinator || '{}');

  return (
    <div className="group-details-container">
      <div className="group-modal-header">
        <h2>{group.title}</h2>
        <button onClick={onClose} className="close-modal-btn">×</button>
      </div>

      {/* Tabs */}
      <div className="group-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'participants' ? 'active' : ''}`}
          onClick={() => setActiveTab('participants')}
        >
          Participants ({participants.length})
        </button>
        <button
          className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          Payments
        </button>
      </div>

      <div className="group-modal-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="group-overview">
            <div className="group-details-section">
              <h3>Trip Details</h3>
              <div className="group-info-grid">
                <div className="info-item">
                  <span className="info-label">Destination</span>
                  <span className="info-value">{group.destination}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Travel Dates</span>
                  <span className="info-value">
                    {formatDate(group.start_date)} - {formatDate(group.end_date)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Component Type</span>
                  <span className="info-value">{group.component_type}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Max Participants</span>
                  <span className="info-value">
                    {group.max_participants || 'Unlimited'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Current Participants</span>
                  <span className="info-value">{group.participant_count || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Total Revenue</span>
                  <span className="info-value">{formatCurrency(group.total_revenue)}</span>
                </div>
              </div>
            </div>

            <div className="group-details-section">
              <h3>Packages</h3>
              <div className="packages-list">
                {packages.map((pkg, index) => (
                  <div key={index} className="package-info">
                    <h4>{pkg.name}</h4>
                    <p className="package-price">{formatCurrency(pkg.price)} per person</p>
                    {pkg.description && <p className="package-description">{pkg.description}</p>}
                    {pkg.inclusions && pkg.inclusions.length > 0 && (
                      <div className="package-inclusions">
                        <strong>Includes:</strong>
                        <ul>
                          {pkg.inclusions.map((inclusion, i) => (
                            <li key={i}>{inclusion}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {coordinator.name && (
              <div className="group-details-section">
                <h3>Group Coordinator</h3>
                <div className="coordinator-info">
                  <p><strong>Name:</strong> {coordinator.name}</p>
                  {coordinator.email && <p><strong>Email:</strong> {coordinator.email}</p>}
                  {coordinator.phone && <p><strong>Phone:</strong> {coordinator.phone}</p>}
                </div>
              </div>
            )}

            {group.cancellation_policy && (
              <div className="group-details-section">
                <h3>Cancellation Policy</h3>
                <p>{group.cancellation_policy}</p>
              </div>
            )}

            <div className="group-details-section">
              <h3>Public Booking URL</h3>
              <div className="public-url-section">
                <input
                  type="text"
                  value={group.public_url}
                  readOnly
                  className="public-url-input"
                />
                <button
                  onClick={() => {
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(group.public_url);
                      alert('URL copied to clipboard!');
                    }
                  }}
                  className="copy-url-btn"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Participants Tab */}
        {activeTab === 'participants' && (
          <div className="group-participants">
            <div className="participants-header">
              <h3>Group Participants</h3>
              <button
                onClick={() => setShowAddParticipant(true)}
                className="btn btn-primary"
              >
                Add Participant
              </button>
            </div>

            {loading ? (
              <div className="loading">Loading participants...</div>
            ) : participants.length === 0 ? (
              <div className="empty-state">
                <p>No participants yet</p>
                <button
                  onClick={() => setShowAddParticipant(true)}
                  className="btn btn-primary mt-2"
                >
                  Add First Participant
                </button>
              </div>
            ) : (
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Package</th>
                    <th>Amount</th>
                    <th>Payment Status</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map((participant) => (
                    <tr key={participant.id}>
                      <td>{participant.client_name}</td>
                      <td>{participant.client_email}</td>
                      <td>{participant.client_phone}</td>
                      <td>{participant.package_selected}</td>
                      <td>{formatCurrency(participant.total_amount)}</td>
                      <td>
                        <span className={getPaymentStatusBadge(participant.payment_status)}>
                          {participant.payment_status}
                        </span>
                      </td>
                      <td>{formatDate(participant.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Add Participant Form */}
            {showAddParticipant && (
              <div className="add-participant-modal">
                <div className="modal-header">
                  <h4>Add Participant</h4>
                  <button
                    onClick={() => setShowAddParticipant(false)}
                    className="close-btn"
                  >
                    ×
                  </button>
                </div>
                <form onSubmit={handleAddParticipant} className="participant-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Name *</label>
                      <input
                        type="text"
                        value={participantForm.client_name}
                        onChange={(e) => setParticipantForm({
                          ...participantForm,
                          client_name: e.target.value
                        })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        value={participantForm.client_email}
                        onChange={(e) => setParticipantForm({
                          ...participantForm,
                          client_email: e.target.value
                        })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={participantForm.client_phone}
                        onChange={(e) => setParticipantForm({
                          ...participantForm,
                          client_phone: e.target.value
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Package *</label>
                      <select
                        value={participantForm.package_selected}
                        onChange={(e) => {
                          const selectedPackage = packages.find(p => p.name === e.target.value);
                          setParticipantForm({
                            ...participantForm,
                            package_selected: e.target.value,
                            total_amount: selectedPackage ? selectedPackage.price : 0
                          });
                        }}
                        required
                      >
                        <option value="">Select Package</option>
                        {packages.map((pkg, index) => (
                          <option key={index} value={pkg.name}>
                            {pkg.name} - {formatCurrency(pkg.price)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Total Amount</label>
                      <input
                        type="number"
                        value={participantForm.total_amount}
                        onChange={(e) => setParticipantForm({
                          ...participantForm,
                          total_amount: e.target.value
                        })}
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="button" onClick={() => setShowAddParticipant(false)} className="btn-secondary">
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      Add Participant
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="group-payments">
            <div className="group-details-section">
              <h3>Payment Schedule</h3>
              <div className="payment-schedule-list">
                {paymentSchedule.map((payment, index) => (
                  <div key={index} className="payment-schedule-item">
                    <div className="payment-info">
                      <h4>{payment.name}</h4>
                      <p>{payment.percentage}% of total</p>
                      {payment.due_date && <p>Due: {formatDate(payment.due_date)}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="group-details-section">
              <h3>Payment Summary</h3>
              <div className="payment-summary">
                <div className="summary-item">
                  <span>Total Revenue:</span>
                  <span className="amount">{formatCurrency(group.total_revenue)}</span>
                </div>
                <div className="summary-item">
                  <span>Total Participants:</span>
                  <span>{participants.length}</span>
                </div>
                <div className="summary-item">
                  <span>Average per Participant:</span>
                  <span className="amount">
                    {formatCurrency(participants.length > 0 ? group.total_revenue / participants.length : 0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="group-details-section">
              <h3>Payment Status by Participant</h3>
              <div className="payment-status-summary">
                <div className="status-item">
                  <span className="status-label">Paid:</span>
                  <span className="status-count">
                    {participants.filter(p => p.payment_status === 'paid').length}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Pending:</span>
                  <span className="status-count">
                    {participants.filter(p => p.payment_status === 'pending').length}
                  </span>
                </div>
                <div className="status-item">
                  <span className="status-label">Partial:</span>
                  <span className="status-count">
                    {participants.filter(p => p.payment_status === 'partial').length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupDetails;
